import { useState, useEffect } from "react";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../Hooks/useTokenRefresh";
import httpclient from "../../../Utils";
import dayjs from "dayjs";
import { buildApiQuery } from "../../../Utils/apiQueryBuilder";

export const fieldMappings = {
  isNotified: ["is_sent", "$eq"],
  isCustomTemplate: ["is_allow_custom_template", "$eq"],
};

export const rangeFields = {
  notifiedDate: {
    apiField: "sent_at",
    minField: "notifiedDateStart",
    maxField: "notifiedDateEnd",
  },
};

export const valueTransformers = {
  // Convert boolean active to 1/0
  isNotified: (value) => {
    if (value === "Yes") return 1;
    if (value === "No") return 0;
    return value; // Return original value if not boolean
  },
  isCustomTemplate: (value) => {
    if (value === "Yes") return 1;
    if (value === "No") return 0;
    return value;
  },
  notifiedDateStart: (value) =>
    value ? dayjs(value).format("YYYY-MM-DD") : "",
  notifiedDateEnd: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
};

export const useCustomNotification = (options = {}) => {
  const {
    autoFetch = true,
    pagination = { page: 0, pageSize: 20 },
    sorting = [],
    transformData = null,
  } = options;

  const [notifications, setNotifications] = useState([]);
  const [userList, setUserList] = useState([]);
  const [allowAdd, setAllowAdd] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [submittedData, setSubmittedData] = useState({});
  

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = buildApiQuery("/custom-notifications", {
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        filters: submittedData,
        sortModel: sorting,
        fieldMappings,
        rangeFields,
        valueTransformers,
      });

      const res = await httpclient.get(apiUrl);
      const { data } = res;
console.log ("The notification data check", data)
      if (data.status === 200) {
        const notificationsData = transformData
          ? transformData(data.data)
          : data.data;
        setNotifications(notificationsData);
        setUserList(data.user_list || []);
        setAllowAdd(data.allow_add || 0); // Set allow_add from response
        setRowCount(data.meta?.total || data.count || 0);
      } else {
        const errorMsg = data.message || "Failed to fetch notifications";
        setError(errorMsg);
        showNotification("error", errorMsg);
      }
    } catch (error) {
      const errorMsg = "Failed to fetch notifications";
      setError(errorMsg);

      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchNotifications();
    }
  }, [
    autoFetch,
    pagination.page,
    pagination.pageSize,
    JSON.stringify(sorting),
    JSON.stringify(submittedData),
  ]);

  const refreshNotifications = () => {
    fetchNotifications();
  };

  return {
    notifications,
    setNotifications,
    userList,
    setUserList,
    allowAdd,
    setAllowAdd,
    loading,
    setLoading,
    error,
    rowCount,
    submittedData,
    setSubmittedData,
    fetchNotifications,
    refresh,
    refreshNotifications,
  };
};
